import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CancelIcon from '@mui/icons-material/Cancel';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PetsIcon from '@mui/icons-material/Pets';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getHotelInfo, getHotelPolicies } from './nexastayApi';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';

const POLICY_ICON_MAP = {
  terms_and_conditions: PolicyIcon,
  privacy_policy: PrivacyTipIcon,
  child_policy: ChildCareIcon,
  cancellation_policy: CancelIcon,
  refund_policy: CancelIcon,
  check_in_policy: LoginIcon,
  check_out_policy: LogoutIcon,
  pet_policy: PetsIcon,
  smoking_policy: SmokingRoomsIcon,
  payment_policy: PaymentIcon,
  age_restriction: InfoOutlinedIcon,
  age_restriction_policy: InfoOutlinedIcon,
  other: MenuBookIcon,
};

function getPolicyIcon(policyType) {
  return POLICY_ICON_MAP[policyType] || PolicyIcon;
}

export default function TermsPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const theme = useNexaStayTheme();
  const isDark = theme?.mode === 'dark';
  const accent = theme?.accent || '#4ce1b6';

  const [hotel, setHotel] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    (async () => {
      try {
        const [infoRes, policiesRes] = await Promise.all([
          getHotelInfo(hotelId),
          getHotelPolicies(hotelId),
        ]);
        if (!cancelled) {
          setHotel(infoRes);
          setPolicies(policiesRes.policies || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load terms and policies.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
          gap: 2,
        }}
      >
        <CircularProgress size={48} sx={{ color: accent }} />
        <Typography variant="body2" sx={{ color: theme?.textSecondary }}>Loading terms & policies…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 4 }}>
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { color: theme?.textPrimary },
          }}
        >
          {error}
        </Alert>
        <Button
          component={Link}
          to={nexastayPath(basePath, hotelId)}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{
            color: accent,
            borderColor: alpha(accent, 0.5),
            textTransform: 'none',
            '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.08) },
          }}
        >
          Back to home
        </Button>
      </Box>
    );
  }

  const hotelName = hotel?.name || 'Hotel';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      {/* Back */}
      <Button
        component={Link}
        to={nexastayPath(basePath, hotelId)}
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        variant="outlined"
        size="small"
        sx={{
          color: theme?.textSecondary,
          borderColor: theme?.borderAccent,
          textTransform: 'none',
          borderRadius: 3,
          mb: 3,
          '&:hover': {
            borderColor: accent,
            color: accent,
            bgcolor: alpha(accent, 0.06),
          },
        }}
      >
        Back to home
      </Button>

      {/* Hero header */}
      <Box
        sx={{
          mb: 5,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 100%)`
            : `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.06)} 100%)`,
          border: `1px solid ${alpha(accent, isDark ? 0.2 : 0.3)}`,
          boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.15)' : '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accent, 0.2),
              color: accent,
              border: `1px solid ${alpha(accent, 0.4)}`,
            }}
          >
            <MenuBookIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ color: theme?.textPrimary, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              Terms & Policies
            </Typography>
            <Typography variant="body1" sx={{ color: theme?.textSecondary, mt: 0.5, fontWeight: 500 }}>
              {hotelName}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ color: theme?.textSecondary, opacity: 0.9, maxWidth: 560 }}>
          Please read the following before making a booking. By reserving with us, you agree to these terms and policies.
        </Typography>
      </Box>

      {policies.length === 0 ? (
        <Box
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: isDark ? alpha(theme?.paper, 0.6) : alpha(accent, 0.04),
            border: `1px dashed ${theme?.borderAccent}`,
          }}
        >
          <PolicyIcon sx={{ fontSize: 64, color: theme?.textMuted, opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" sx={{ color: theme?.textPrimary, fontWeight: 600, mb: 1 }}>
            No policies published yet
          </Typography>
          <Typography variant="body2" sx={{ color: theme?.textSecondary, maxWidth: 400, mx: 'auto', mb: 3 }}>
            This property hasn’t added terms or policies here yet. For cancellation, check-in, or other rules, please contact the hotel directly.
          </Typography>
          <Button
            component={Link}
            to={nexastayPath(basePath, hotelId, 'contact')}
            variant="outlined"
            size="medium"
            sx={{
              color: accent,
              borderColor: alpha(accent, 0.5),
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.08) },
            }}
          >
            Contact hotel
          </Button>
        </Box>
      ) : (
        <Stack spacing={2}>
          {policies.map((policy, index) => {
            const IconComponent = getPolicyIcon(policy.policy_type);
            return (
              <Accordion
                key={policy.policy_type || index}
                defaultExpanded={index === 0}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${theme?.borderAccent}`,
                  bgcolor: isDark ? alpha(theme?.paper, 0.5) : theme?.paper,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { my: 0, mb: 0 },
                  '& + &': { mt: 2 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: accent }} />}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    minHeight: 72,
                    '& .MuiAccordionSummary-content': { my: 2 },
                    '&.Mui-expanded': { minHeight: 72 },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(accent, 0.15),
                        color: accent,
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ color: theme?.textPrimary, fontWeight: 700 }}>
                        {policy.title}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25, flexWrap: 'wrap', gap: 0.5 }}>
                        {policy.policy_type_display && (
                          <Chip
                            label={policy.policy_type_display}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: alpha(accent, 0.12),
                              color: accent,
                              border: 'none',
                            }}
                          />
                        )}
                        {policy.version && (
                          <Chip
                            label={`v${policy.version}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              borderColor: theme?.borderAccent,
                              color: theme?.textSecondary,
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
                  {policy.summary && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme?.textSecondary,
                        fontStyle: 'italic',
                        mb: 2,
                        pl: 2,
                        borderLeft: `3px solid ${alpha(accent, 0.5)}`,
                      }}
                    >
                      {policy.summary}
                    </Typography>
                  )}
                  {policy.content && (
                    <Box
                      component="div"
                      className="nexastay-policy-content"
                      sx={{
                        color: theme?.textPrimary,
                        '& p': { mb: 1.5, lineHeight: 1.75 },
                        '& ul, & ol': { pl: 2.5, mb: 1.5, lineHeight: 1.7 },
                        '& li': { mb: 0.5 },
                        '& a': { color: accent, textDecoration: 'underline', '&:hover': { opacity: 0.9 } },
                        '& strong': { fontWeight: 700, color: theme?.textPrimary },
                        '& h3, & h4': { mt: 2, mb: 1, fontWeight: 700, color: theme?.textPrimary },
                      }}
                      dangerouslySetInnerHTML={{ __html: policy.content }}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
